/**
 * Session storage for login-as-customer (impersonation) origin.
 * Used to return the user to the customer detail page after they log out from the customer session.
 * Uses sessionStorage only; no long-term persistence.
 */

const IMPERSONATION_ORIGIN_KEY = "impersonationOrigin";

/**
 * @param {Object} opts
 * @param {string} opts.originRoute - Full path to return to (e.g. /dsok/customer-detail/:customerHash/settings)
 * @param {string} opts.originCustomerHash - Customer hash/url from the route
 * @param {string|number} [opts.originUserId] - Current admin/partner user id
 * @param {string|number} [opts.originUserType] - Current admin/partner user type/role
 * @param {string} [opts.originTab] - Current tab/section if available
 */
export function setImpersonationOrigin(opts) {
  if (typeof sessionStorage === "undefined") return;
  const payload = {
    isImpersonation: true,
    originRoute: opts?.originRoute ?? "",
    originCustomerHash: opts?.originCustomerHash ?? "",
    originUserId: opts?.originUserId,
    originUserType: opts?.originUserType,
    originTab: opts?.originTab,
    createdAt: new Date().toISOString(),
  };
  try {
    sessionStorage.setItem(IMPERSONATION_ORIGIN_KEY, JSON.stringify(payload));
  } catch (_e) {
    // ignore
  }
}

/**
 * @returns {Object|null} Parsed impersonation origin or null
 */
export function getImpersonationOrigin() {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_ORIGIN_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

export function clearImpersonationOrigin() {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(IMPERSONATION_ORIGIN_KEY);
  } catch (_e) {
    // ignore
  }
}

/**
 * Valid for return navigation: has isImpersonation and a usable originRoute.
 * @param {Object} obj - Result of getImpersonationOrigin()
 * @returns {boolean}
 */
export function isValidImpersonationOrigin(obj) {
  if (!obj || typeof obj !== "object") return false;
  if (obj.isImpersonation !== true) return false;
  const route = obj.originRoute;
  if (typeof route !== "string" || route.trim() === "") return false;
  // Must look like customer-detail route to avoid open redirect
  if (!/^\/dsok\/customer-detail\/[^/]+(\/.*)?$/.test(route.trim())) return false;
  return true;
}
